import React, { useContext, useRef, useState } from "react";
import { Box, Popover, Slider, Typography } from "@mui/material";

import { getDrawToolPreferences, saveDrawToolPreferences } from "fond/draw/drawToolPreferences";
import { MagnetIcon } from "fond/svg_icons";

import { MapContext } from "../MapProvider";

import ToggleOptionButton from "./ToggleOptionButton";
import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";

// Map evenly spaced slider positions to actual snap distance values
const snapDistanceValues = [5, 10, 20, 30];
const snapDistanceScale = (index: number) => snapDistanceValues[index];
const snapDistanceScaleInverse = (value: number) => snapDistanceValues.indexOf(value);

const snapDistanceMarks = [{ value: 0 }, { value: 1 }, { value: 2 }, { value: 3 }];

const SnappingButton: React.FC<IButtonProps> = ({ icon, title, ...rest }) => {
  const { drawControl, map } = useContext(MapContext);
  const [snap, setSnap] = useState(drawControl.current?.options?.snap || false);
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Local state for snap distance — prefer the active draw control value (which
  // may have been overridden by a handler), falling back to persisted preferences.
  const [snapPx, setSnapPx] = useState(() => drawControl.current?.options?.snapOptions?.snapPx ?? getDrawToolPreferences().snapOptions.snapPx);

  const handleOnChange = () => {
    const currentValue = !!drawControl.current?.options?.snap;
    const newValue = !currentValue;

    setSnap(newValue);
    map?.fire("draw.options_changed", { snap: newValue });
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOptionsAnchor(optionsAnchor ? null : containerRef.current);
  };

  const handleCloseOptions = () => {
    setOptionsAnchor(null);
  };

  const fireSnapOptions = (patch: Record<string, unknown>) => {
    const current = drawControl.current?.options?.snapOptions ?? {};
    map?.fire("draw.options_changed", {
      snapOptions: { ...current, ...patch },
    });
    saveDrawToolPreferences({ snapOptions: patch });
  };

  return (
    <Box ref={containerRef} sx={{ display: "flex", flexDirection: "column" }}>
      <ToolbarToggleButton
        title={title || `${snap ? "Disable" : "Enable"} snapping`}
        {...rest}
        value="snap"
        onChange={handleOnChange}
        selected={snap}
        hasOptions
        data-testid="toolbar-snap-button"
      >
        {icon || <MagnetIcon />}
      </ToolbarToggleButton>

      <ToggleOptionButton onClick={handleOptionsClick} active={!!optionsAnchor} selected={snap} title="Snap options" />

      <Popover
        open={!!optionsAnchor}
        anchorEl={optionsAnchor}
        onClose={handleCloseOptions}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: { p: 2, width: 280 },
          },
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
          Snap Options
        </Typography>

        <Typography variant="caption" color="text.secondary" fontWeight={600}>
          Snap to features when the cursor is within:
        </Typography>
        <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, mb: -1 }}>
          {snapDistanceValues.map((value) => (
            <Typography key={value} variant="caption" color="text.secondary">
              {value}px
            </Typography>
          ))}
        </Box>
        <Box>
          <Slider
            size="small"
            aria-label="Snap distance"
            value={snapDistanceScaleInverse(snapPx)}
            min={0}
            max={3}
            step={null}
            scale={snapDistanceScale}
            marks={snapDistanceMarks}
            valueLabelDisplay="auto"
            onChange={(_, v) => {
              const val = snapDistanceScale(v as number);
              setSnapPx(val);
              fireSnapOptions({ snapPx: val });
            }}
          />
        </Box>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1, mt: -1 }}>
          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            Close
          </Typography>
          <Typography variant="caption" color="text.secondary" fontStyle="italic">
            Far
          </Typography>
        </Box>
      </Popover>
    </Box>
  );
};

export default SnappingButton;
